import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { ScheduledPostTarget } from 'types'
import { getPlatformDisplayName } from 'utils/platformDisplayNames'
import { PLATFORM_CHARACTER_LIMITS, type PlatformLimits } from 'components/ValidationBanner/utils'
import { getFriendlyErrorMessage } from '../Toolbar/postNotificationBannerConfig'
import { hasContentWarning } from './channelWarnings'

export type PreviewAlertAction = 'refresh-connection' | 'adjust-caption'

export interface PreviewAlertArgs {
  almanacFile: AlmanacFileShowItem
  disconnectedIntegrations: ExternalIntegration[]
}

export interface PreviewAlert {
  variant: 'error' | 'warning'
  message: string
  actionLabel: string
  action: PreviewAlertAction
  target: ScheduledPostTarget | null
  testId: string
}

export interface PreviewAlertConfig {
  testId: string
  isRendered: (args: PreviewAlertArgs) => ScheduledPostTarget | null
  build: (args: PreviewAlertArgs, target: ScheduledPostTarget) => PreviewAlert
}

const getTargets = (almanacFile: AlmanacFileShowItem): ScheduledPostTarget[] => almanacFile.scheduled_post_targets ?? []

export const previewAlertBannerConfigs: PreviewAlertConfig[] = [
  {
    testId: 'connection-expired',
    isRendered: ({ almanacFile, disconnectedIntegrations }) => {
      if (!disconnectedIntegrations.length) return null
      const targets = getTargets(almanacFile)
      if (!targets.length) return null

      const allPublished = targets.every((t) => t.status === 'published')
      if (allPublished) return null

      const disconnectedIds = new Set(disconnectedIntegrations.map((i) => i.id))
      return targets.find((t) => t.status === 'failed' && disconnectedIds.has(t.external_integration_id)) ?? null
    },
    build: (_args, target) => ({
      variant: 'error',
      message: `Your ${getPlatformDisplayName(target.provider)} connection expired.`,
      actionLabel: 'Refresh Connection',
      action: 'refresh-connection',
      target,
      testId: 'connection-expired',
    }),
  },
  {
    testId: 'failed-target',
    isRendered: ({ almanacFile }) => {
      const targets = getTargets(almanacFile)
      return targets.find((t) => t.status === 'failed') ?? null
    },
    build: (_args, target) => {
      const platform = getPlatformDisplayName(target.provider)
      const friendly = target.error_message ? getFriendlyErrorMessage(target.error_message, target.provider) : null
      const message = friendly?.message ?? `${platform} couldn't post.`
      return {
        variant: 'error',
        message,
        actionLabel: 'Refresh Connection',
        action: 'refresh-connection',
        target,
        testId: 'failed-target',
      }
    },
  },
  {
    testId: 'content-warning',
    isRendered: ({ almanacFile }) => {
      if (almanacFile.post_status !== PostStatusPropertyValue.AutoPostScheduled) return null
      const targets = getTargets(almanacFile)
      return targets.find((t) => hasContentWarning(t)) ?? null
    },
    build: (_args, target) => {
      const platform = getPlatformDisplayName(target.provider)
      const key = target.provider as PlatformLimits
      const limit = PLATFORM_CHARACTER_LIMITS[key]?.limit
      return {
        variant: 'warning',
        message: `Caption is too long for ${platform} — trim it down to ${limit} characters.`,
        actionLabel: 'Adjust Caption',
        action: 'adjust-caption',
        target,
        testId: 'content-warning',
      }
    },
  },
]

export const derivePreviewAlert = (args: PreviewAlertArgs): PreviewAlert | null => {
  for (const config of previewAlertBannerConfigs) {
    const target = config.isRendered(args)
    if (target) return config.build(args, target)
  }
  return null
}
